import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Clock,
  CheckCircle2,
  FileText,
  Hourglass,
  TrendingUp,
  ArrowRight,
  AlertCircle,
  Video,
  CalendarSync,
  Target,
} from "lucide-react";
import { PageHeader } from "@/components/layout/PageHeader";
import { api, type DashboardData } from "@/lib/api";
import { usePeriod } from "@/lib/period";
import type { GoogleEvent, Prospect } from "@shared/schema";
import {
  cn,
  formatDate,
  formatEUR,
  formatRelativeDate,
  fullName,
  initials,
} from "@/lib/utils";
import { StatusPill } from "@/components/ui/StatusPill";
import { AvanceTag } from "@/components/ui/AvanceTag";
import { PRODUCTS } from "@shared/products";
import { COMMISSION_TEXT_COLOR, TONE_STYLES } from "@/lib/colors";
import { getRdvTone } from "@/lib/rdv";

export function DashboardPage() {
  const [data, setData] = useState<DashboardData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [sessions, setSessions] = useState<
    Array<GoogleEvent & { prospect: Prospect | null }> | null
  >(null);
  const [prestationsSummary, setPrestationsSummary] = useState<{
    invoiced: number;
    pending: number;
    paid: number;
  } | null>(null);

  const { bounds, label } = usePeriod();
  const win = bounds();
  const winKey = win ? `${win.from}_${win.to}` : "all";
  const periodLabel = label();

  useEffect(() => {
    let active = true;
    const winParams = win ?? {};
    api
      .dashboard(winParams)
      .then((d) => active && setData(d))
      .catch((e) => active && setError(e instanceof Error ? e.message : "Erreur"));
    api
      .googleEvents({ upcoming: true, limit: 6 })
      .then((r) => active && setSessions(r.events))
      .catch(() => active && setSessions([]));
    api
      .prestationsSummary(winParams)
      .then((r) => active && setPrestationsSummary(r.summary))
      .catch(() => active && setPrestationsSummary({ invoiced: 0, pending: 0, paid: 0 }));
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [winKey]);

  if (error) {
    return (
      <div className="surface p-6">
        <p className="text-[var(--color-coral-600)]">{error}</p>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-64 grid place-items-center">
        <div className="h-8 w-8 rounded-full border-2 border-[var(--color-navy-600)] border-r-transparent animate-spin" />
      </div>
    );
  }

  const { summary, followUps, recentSales, byStatus } = data;

  return (
    <>
      <PageHeader
        title="Dashboard"
        subtitle="Vue d'ensemble des commissions et des relances en cours"
      />

      {/* KPI cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 lg:gap-4 mb-6">
        <KpiCard
          icon={TrendingUp}
          tone="navy"
          label="Volume ventes"
          value={formatEUR(summary.totalSalesVolume)}
          hint="Sur les ventes en cours"
        />
        <KpiCard
          icon={Hourglass}
          tone="yellow"
          label="Attente rétractation"
          value={formatEUR(summary.pendingRetractation)}
          hint="Commission gelée 14 j après close"
        />
        <KpiCard
          icon={Clock}
          tone="coral"
          label="Dû"
          value={formatEUR(summary.due)}
          hint="Comm. + presta à encaisser"
        />
        <KpiCard
          icon={CheckCircle2}
          tone="green"
          label="Encaissé"
          value={formatEUR(summary.paid)}
          hint={`Comm. + presta · ${periodLabel}`}
        />
      </div>

      {sessions && sessions.length > 0 && (
        <section className="surface p-5 mb-4 lg:mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-[var(--color-navy-700)] flex items-center gap-2">
              <CalendarSync size={18} /> Prochaines sessions
            </h2>
            <Link
              to="/parametres"
              className="text-xs text-[var(--color-muted-foreground)] hover:text-[var(--color-navy-700)]"
            >
              Sync via Google Calendar
            </Link>
          </div>
          <ul className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {sessions.slice(0, 6).map((s) => {
              const start = new Date(s.startTime);
              const tone = TONE_STYLES[getRdvTone(s, s.prospect?.status)];
              return (
                <li
                  key={s.id}
                  className={cn(
                    "border rounded-[var(--radius-md)] p-3 border-l-4",
                    tone.cardBg,
                    tone.cardBorder,
                    tone.border,
                  )}
                >
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <span
                      className={cn(
                        "text-xs font-semibold uppercase tracking-wide tabular",
                        tone.pillFg,
                      )}
                    >
                      {start.toLocaleDateString("fr-FR", {
                        weekday: "short",
                        day: "2-digit",
                        month: "short",
                      })}
                      {" · "}
                      {start.toLocaleTimeString("fr-FR", {
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </span>
                    {s.meetingUrl && (
                      <a
                        href={s.meetingUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="text-[var(--color-navy-600)] hover:text-[var(--color-coral-600)]"
                        aria-label="Lien visio"
                      >
                        <Video size={14} />
                      </a>
                    )}
                  </div>
                  {s.prospect ? (
                    <Link
                      to={`/prospects/${s.prospect.id}`}
                      className="font-medium text-[var(--color-navy-700)] hover:text-[var(--color-coral-600)] block truncate"
                    >
                      {fullName(s.prospect)}
                    </Link>
                  ) : (
                    <span className="font-medium text-[var(--color-navy-700)] block truncate">
                      {s.attendeeName ?? s.attendeeEmail ?? "Sans invité"}
                    </span>
                  )}
                  <p className="text-xs text-[var(--color-muted-foreground)] truncate mt-0.5">
                    {s.summary}
                  </p>
                </li>
              );
            })}
          </ul>
        </section>
      )}

      {prestationsSummary && prestationsSummary.invoiced > 0 && (
        <section className="surface p-4 mb-4 lg:mb-6">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-[var(--color-navy-700)] flex items-center gap-2">
              <FileText size={16} /> Prestations & créances
            </h3>
            <Link
              to="/prestations"
              className="text-xs text-[var(--color-muted-foreground)] hover:text-[var(--color-navy-700)]"
            >
              Voir tout →
            </Link>
          </div>
          <div className="grid grid-cols-3 gap-3 text-sm">
            <div>
              <div className="text-xs text-[var(--color-muted-foreground)] uppercase tracking-wide">
                Facturé
              </div>
              <div className="font-bold tabular text-[var(--color-navy-700)] mt-0.5">
                {formatEUR(prestationsSummary.invoiced)}
              </div>
            </div>
            <div>
              <div className="text-xs text-[var(--color-muted-foreground)] uppercase tracking-wide">
                En attente
              </div>
              <div className="font-bold tabular text-[#92520a] mt-0.5">
                {formatEUR(prestationsSummary.pending)}
              </div>
            </div>
            <div>
              <div className="text-xs text-[var(--color-muted-foreground)] uppercase tracking-wide">
                Payé
              </div>
              <div className="font-bold tabular text-[#0f6b30] mt-0.5">
                {formatEUR(prestationsSummary.paid)}
              </div>
            </div>
          </div>
        </section>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 lg:gap-6">
        {/* Follow-ups */}
        <section className="surface p-5 lg:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-[var(--color-navy-700)]">
              Relances à venir
            </h2>
            <Link
              to="/todo"
              className="text-sm text-[var(--color-navy-600)] hover:text-[var(--color-coral-600)] flex items-center gap-1"
            >
              Voir la to-do <ArrowRight size={14} />
            </Link>
          </div>
          {followUps.length === 0 ? (
            <p className="text-sm text-[var(--color-muted-foreground)]">
              Aucune relance planifiée.
            </p>
          ) : (
            <ul className="divide-y divide-[var(--color-border)] -mx-5">
              {followUps.slice(0, 8).map((p) => (
                <li key={p.id}>
                  <Link
                    to={`/prospects/${p.id}`}
                    className="flex items-center gap-3 px-5 py-3 hover:bg-[var(--color-surface-2)] transition-colors"
                  >
                    <div className="avatar w-9 h-9">
                      {initials(fullName(p))}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="font-medium text-[var(--color-navy-700)] truncate">
                        {fullName(p)}
                      </div>
                      <div className="text-xs text-[var(--color-muted-foreground)] truncate">
                        {p.context ?? "—"}
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-1.5 flex-shrink-0">
                      <span
                        className={cn(
                          "text-sm font-medium tabular",
                          p.overdue
                            ? "text-[var(--color-coral-600)]"
                            : "text-[var(--color-navy-700)]",
                        )}
                      >
                        {p.overdue && (
                          <AlertCircle
                            size={14}
                            className="inline mr-1 -mt-0.5"
                          />
                        )}
                        {formatRelativeDate(p.nextFollowUpAt)}
                      </span>
                      <StatusPill status={p.status} />
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </section>

        {/* Recent sales */}
        <section className="surface p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-[var(--color-navy-700)]">
              Ventes récentes
            </h2>
            <Link
              to="/ventes"
              className="text-sm text-[var(--color-navy-600)] hover:text-[var(--color-coral-600)] flex items-center gap-1"
            >
              Voir tout <ArrowRight size={14} />
            </Link>
          </div>
          {recentSales.length === 0 ? (
            <p className="text-sm text-[var(--color-muted-foreground)]">
              Pas encore de vente enregistrée.
            </p>
          ) : (
            <ul className="space-y-3">
              {recentSales.map((s) => (
                <li
                  key={s.id}
                  className="flex items-start justify-between gap-3"
                >
                  <div className="min-w-0">
                    <div className="flex items-center gap-1.5">
                      <Link
                        to={`/ventes`}
                        className="font-medium text-[var(--color-navy-700)] hover:text-[var(--color-coral-600)] truncate"
                      >
                        {s.prospect ? fullName(s.prospect) : "—"}
                      </Link>
                      {s.inAdvance && <AvanceTag />}
                    </div>
                    <div className="text-xs text-[var(--color-muted-foreground)]">
                      {PRODUCTS[s.product].label} ·{" "}
                      {s.paymentPlan === 1
                        ? "1x"
                        : `${s.paymentPlan}x`} · {formatDate(s.closedAt)}
                    </div>
                  </div>
                  <div className="text-right tabular flex-shrink-0">
                    <div className="font-semibold text-[var(--color-navy-700)]">
                      {formatEUR(s.totalAmount)}
                    </div>
                    <div
                      className={cn(
                        "text-xs font-medium",
                        COMMISSION_TEXT_COLOR[s.commissionStatus],
                      )}
                    >
                      +{formatEUR(s.totalAmount * s.commissionRate)}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}

          <div className="mt-6 pt-5 border-t border-[var(--color-border)]">
            <h3 className="text-xs uppercase tracking-wide font-medium text-[var(--color-muted-foreground)] mb-2">
              Pipeline
            </h3>
            <div className="flex items-center justify-between gap-3 rounded-[var(--radius-md)] bg-[var(--color-surface-2)] px-3 py-2.5 mb-3">
              <div className="flex items-center gap-2 min-w-0">
                <Target
                  size={16}
                  className="text-[var(--color-navy-600)] flex-shrink-0"
                />
                <span className="text-sm font-medium text-[var(--color-navy-700)]">
                  Taux de closing
                </span>
              </div>
              <div className="text-right flex-shrink-0">
                <div className="text-lg font-bold tabular text-[var(--color-navy-700)] leading-tight">
                  {Math.round(summary.closingRatio * 100)} %
                </div>
                <div className="text-xs text-[var(--color-muted-foreground)]">
                  {summary.closedCount} closés sur {summary.prospectsTotal} prospects avec RDV
                </div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              {Object.entries(byStatus).map(([status, count]) => (
                <div
                  key={status}
                  className="flex items-center justify-between text-sm py-1.5"
                >
                  <StatusPill status={status as never} />
                  <span className="font-medium tabular">{count}</span>
                </div>
              ))}
            </div>
          </div>
        </section>
      </div>
    </>
  );
}

function KpiCard({
  icon: Icon,
  tone,
  label,
  value,
  hint,
}: {
  icon: React.ComponentType<{ size?: number; className?: string }>;
  tone: "navy" | "coral" | "yellow" | "green";
  label: string;
  value: string;
  hint?: string;
}) {
  // KpiCard icons sit on a solid colored chip. TONE_STYLES.dot is exactly that
  // solid color (used elsewhere for pill dots) — reuse it so the KpiCard
  // accent stays in sync with the rest of the palette.
  const toneClass = cn(TONE_STYLES[tone].dot, "text-white");
  return (
    <div className="surface p-4 lg:p-5">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div
          className={cn(
            "h-9 w-9 rounded-lg grid place-items-center flex-shrink-0",
            toneClass,
          )}
        >
          <Icon size={18} />
        </div>
      </div>
      <div className="text-xs font-medium text-[var(--color-muted-foreground)] uppercase tracking-wide">
        {label}
      </div>
      <div className="text-2xl font-bold text-[var(--color-navy-700)] tabular mt-0.5">
        {value}
      </div>
      {hint && (
        <div className="text-xs text-[var(--color-muted-foreground)] mt-1">
          {hint}
        </div>
      )}
    </div>
  );
}
